import { shallowRef, watch } from "vue";
import axios from "axios";
import { appAxios } from "@/core/utils/appAxios";
import {
    GetAuthenticatedVehiclesQueryParams,
    VehicleSortAttribute,
    PaginationQueryParameters,
    SortDirection,
    AuthenticatedVehicleFiltersDto,
    VehicleFilterType,
    VehicleGroupingQueryParameters,
    PublicVehicleFiltersDto,
    AdvancedFilterItemDto,
} from "@/shared/api-contract/api-contract";
import { appLanguageSwitcherModel } from "@/shared/components/app-language-switcher/app-language-switcher.model";
import { appModel } from "@/app.model";
import { sessionModel } from "@/shared/state/session.model";

export const defaultPaginationQueryParameters: PaginationQueryParameters = {
    per_page: 12,
    current_page: 1,
    sort_field: 1,
    sort_direction: 1,
};

export type SortModel = {
    id_field: number;
    id_direction: number;
};

export type VehicleQueryAdvancedParameters = {
    advanced: readonly number[];
};

export const emptyVehicleQueryAdvancedParameters: VehicleQueryAdvancedParameters = {
    advanced: [],
};

export const emptyVehicleQueryParameters: GetAuthenticatedVehiclesQueryParams &
    VehicleQueryAdvancedParameters = {
    condition: [],
    brand: [],
    model: [],
    fueltype: [],
    color: [],
    gearbox: [],
    promotion: [],
    option: [],
    bodywork: [],
    bodytype: [],
    availability: [],
    provider: [],
    emission_nedc_min: null,
    emission_nedc_max: null,
    emission_wltp_min: null,
    emission_wltp_max: null,
    mileage_min: null,
    mileage_max: null,
    damage_amount_min: null,
    damage_amount_max: null,
    firstregistration_year_min: null,
    firstregistration_year_max: null,
    price_min: null,
    price_max: null,
    has_promotion: null,
    ...emptyVehicleQueryAdvancedParameters,
};

export const defaultGroupingQueryParameters: VehicleGroupingQueryParameters = {
    grouping_mode: 1,
    hashcode: null,
};

export const emptyAuthenticatedVehicleFiltersDto: AuthenticatedVehicleFiltersDto = {
    [VehicleFilterType.brand]: [],
    [VehicleFilterType.model]: [],
    [VehicleFilterType.fueltype]: [],
    [VehicleFilterType.condition]: [],
    [VehicleFilterType.color]: [],
    [VehicleFilterType.gearbox]: [],
    [VehicleFilterType.firstregistrationYear]: [],
    [VehicleFilterType.damage]: [],
    [VehicleFilterType.emissionNedc]: [],
    [VehicleFilterType.emissionWltp]: [],
    [VehicleFilterType.mileage]: [],
    [VehicleFilterType.price]: [],
    [VehicleFilterType.availability]: [],
    [VehicleFilterType.provider]: [],
    [VehicleFilterType.bodywork]: [],
    [VehicleFilterType.bodytype]: [],
    [VehicleFilterType.advanced]: [],
};

class VehiclesModel {
    defaultFilters: PaginationQueryParameters &
        VehicleGroupingQueryParameters &
        GetAuthenticatedVehiclesQueryParams = {
        ...defaultPaginationQueryParameters,
        ...defaultGroupingQueryParameters,
        ...emptyVehicleQueryParameters,
    };

    isDefaultFiltersLoaded = shallowRef<boolean>();
    viewDefaultFilters = shallowRef(false);

    constructor() {
        watch(
            () => ({
                accessToken: sessionModel.sessionDto.value,
                lang: appLanguageSwitcherModel.lang.value,
            }),
            async ({ accessToken, lang }) => {
                if (accessToken) {
                    this.isDefaultFiltersLoaded.value = undefined;
                    /* try { TODO: fetch default filters from API per user!
                        const response = await axios.get<{
                            readonly data: PaginationQueryParameters & VehicleGroupingQueryParameters & GetAuthenticatedVehiclesQueryParams | null;
                        }>(`/api/filters?lang=${lang}`, {
                            headers: {
                                Authorization: `Bearer ${accessToken.token}`,
                            },
                        });
                        if (response.data.data) {
                            const x = response.data.data;

                            this.defaultFilters = {
                                ...x,
                                condition: x.condition || emptyVehicleQueryParameters.condition,
                                brand: x.brand || emptyVehicleQueryParameters.brand,
                                model: x.model || emptyVehicleQueryParameters.model,
                                fueltype: x.fueltype || emptyVehicleQueryParameters.fueltype,
                                color: x.color || emptyVehicleQueryParameters.color,
                                emission_nedc_min: x.emission_nedc_min || emptyVehicleQueryParameters.emission_nedc_min,
                                emission_nedc_max: x.emission_nedc_max || emptyVehicleQueryParameters.emission_nedc_max,
                                emission_wltp_min: x.emission_wltp_min || emptyVehicleQueryParameters.emission_wltp_min,
                                emission_wltp_max: x.emission_wltp_max || emptyVehicleQueryParameters.emission_wltp_max,
                                mileage_min: x.mileage_min || emptyVehicleQueryParameters.mileage_min,
                                mileage_max: x.mileage_max || emptyVehicleQueryParameters.mileage_max,
                                gearbox: x.gearbox || emptyVehicleQueryParameters.gearbox,
                                damage_amount_min: x.damage_amount_min || emptyVehicleQueryParameters.damage_amount_min,
                                damage_amount_max: x.damage_amount_max || emptyVehicleQueryParameters.damage_amount_max,
                                firstregistration_year_min: x.firstregistration_year_min || emptyVehicleQueryParameters.firstregistration_year_min,
                                firstregistration_year_max: x.firstregistration_year_max || emptyVehicleQueryParameters.firstregistration_year_max
                            };
                        } else {
                            this.viewDefaultFilters.value = true;
                        }
                    } catch {
                        appModel.toast.value?.add({
                            severity: "error",
                            summary: appModel.i18n.value.t("common.somethingWentWrong"),
                            life: 3000,
                        });
                    } */
                }

                this.isDefaultFiltersLoaded.value = true;
            },
            { immediate: true },
        );
    }

    async saveDefaultFilters(
        filters: PaginationQueryParameters &
            VehicleGroupingQueryParameters &
            GetAuthenticatedVehiclesQueryParams,
    ): Promise<void> {
        /* TO DO: Update to CC API */
        if (!sessionModel.sessionDto.value || !sessionModel.sessionDto.value.token) {
            throw new Error("access token is empty");
        }

        await appAxios.value.post("/api/filters", {
            condition: filters.condition,
            brand: filters.brand,
            model: filters.model,
            fueltype: filters.fueltype,
            color: filters.color,
            emission_nedc_min: filters.emission_nedc_min,
            emission_nedc_max: filters.emission_nedc_max,
            emission_wltp_min: filters.emission_wltp_min,
            emission_wltp_max: filters.emission_wltp_max,
            mileage_min: filters.mileage_min,
            mileage_max: filters.mileage_max,
            gearbox: filters.gearbox,
            damage_amount_min: filters.damage_amount_min,
            damage_amount_max: filters.damage_amount_max,
            firstregistration_year_min: filters.firstregistration_year_min,
            firstregistration_year_max: filters.firstregistration_year_max,
        });

        this.defaultFilters = filters;
    }

    resetDefaultFilters() {
        this.defaultFilters = {
            ...defaultPaginationQueryParameters,
            ...defaultGroupingQueryParameters,
            ...emptyVehicleQueryParameters,
        };
    }
}

export const vehiclesModel = new VehiclesModel();
